







































































































































import { Component, Vue, Watch } from "vue-property-decorator";
import { AIOnboardingService } from "@/shared/services/AIOnboardingService";
import usuarioModule from "@/store/modules/usuario-module";
import menu_optionModule from "@/store/modules/menu_option-module";
import { authorizationService } from "@/shared/services/AuthorizationService";
import { JwtService } from "@/shared/services/JwtService";

interface Message {
  role: "user" | "assistant" | "system";
  content: string;
  timestamp: Date;
}

@Component
export default class OnboardingAIChat extends Vue {
  private service = new AIOnboardingService();
  public expanded = false;
  public unread = false;
  public loading = false;
  public input = "";
  public messages: Message[] = [];

  public get visibleMessages() {
    return this.messages.filter((m) => m.role !== "system");
  }

  public get currentSuggestions() {
    return [
      "¿Cómo funciona esta pantalla?",
      "¿Qué puedo hacer aquí?",
      "Dame un consejo pro",
    ];
  }

  public mounted() {
    this.$root.$on("ai-assistant:open", this.onExternalOpen);
  }

  public beforeDestroy() {
    this.$root.$off("ai-assistant:open", this.onExternalOpen);
  }

  public expand() {
    this.expanded = true;
    this.unread = false;
    this.scrollToBottom();

    // Al expandir, capturamos contexto si no hay nada en la historia más allá del sistema
    if (this.messages.length === 0) {
      this.captureContext();
    }
  }

  public async sendMessage() {
    if (!this.input.trim() || this.loading) return;

    const text = this.input.trim();
    this.input = "";

    this.messages.push({
      role: "user",
      content: text,
      timestamp: new Date(),
    });

    this.loading = true;
    this.scrollToBottom();

    try {
      const response = await this.service.sendMessage(text);

      // Parsear comandos
      const fillRegex = /\[FILL:(.*?):(.*?)\]/g;
      const highlightRegex = /\[HIGHLIGHT:(.*?)\]/g;
      const navigateRegex = /\[NAVIGATE:(.*?)\]/g;
      const clickRegex = /\[CLICK:(.*?)\]/g;

      let match;

      // 1. FILL
      while ((match = fillRegex.exec(response)) !== null) {
        this.$root.$emit("ai:fill-field", {
          field: match[1].trim(),
          value: match[2].trim(),
        });
      }

      // 2. HIGHLIGHT
      while ((match = highlightRegex.exec(response)) !== null) {
        this.$root.$emit("ai:highlight", match[1].trim());
      }

      // 3. NAVIGATE
      while ((match = navigateRegex.exec(response)) !== null) {
        this.$root.$emit("ai:navigate", match[1].trim());
      }

      // 4. CLICK
      while ((match = clickRegex.exec(response)) !== null) {
        this.$root.$emit("ai:click", match[1].trim());
      }

      // Limpiar la respuesta de todas las etiquetas
      const cleanResponse = response
        .replace(fillRegex, "")
        .replace(highlightRegex, "")
        .replace(navigateRegex, "")
        .replace(clickRegex, "")
        .trim();

      this.messages.push({
        role: "assistant",
        content: cleanResponse || "¡Acción ejecutada correctamente!",
        timestamp: new Date(),
      });
    } catch (error) {
      this.messages.push({
        role: "assistant",
        content:
          "❌ Lo siento, he tenido un problema de conexión. Por favor, inténtalo de nuevo en unos momentos.",
        timestamp: new Date(),
      });
    } finally {
      this.loading = false;
      this.scrollToBottom();
    }
  }

  public sendSuggestion(text: string) {
    this.input = text;
    this.sendMessage();
  }

  public resetChat() {
    this.service.reset();
    this.messages = [];
    this.captureContext();
  }

  private onExternalOpen(payload?: any) {
    this.expand();
    if (payload && payload.message) {
      this.input = String(payload.message);
      this.sendMessage();
    }
  }

  private captureContext() {
    const toolbarTitle = document.querySelector(".v-toolbar__title");
    const title =
      (toolbarTitle && toolbarTitle.textContent) ||
      document.title ||
      "Pantalla de la aplicación";

    // 1. Botones (excluyendo los del chat)
    const buttons = Array.from(
      document.querySelectorAll(".v-btn:not(.onboarding-chat-wrapper *)")
    )
      .map((b) => (b.textContent ? b.textContent.trim() : ""))
      .filter((t) => t && t.length > 0)
      .slice(0, 5);

    // 2. Inputs generales (excluyendo los del chat)
    const inputs = Array.from(
      document.querySelectorAll(
        "input:not([type=checkbox]):not([type=radio]):not(.onboarding-chat-wrapper *)"
      )
    )
      .map((i: any) => (i.placeholder || i.name || i.value || "").trim())
      .filter((t) => t && t.length > 0)
      .slice(0, 5);

    // 3. Selects
    const selects = Array.from(
      document.querySelectorAll(
        "select:not(.onboarding-chat-wrapper *), .v-select:not(.onboarding-chat-wrapper *)"
      )
    )
      .map((s: any) => (s.textContent || s.value || "").trim())
      .filter((t) => t && t.length > 0)
      .slice(0, 5);

    // 4. Textareas
    const textareas = Array.from(
      document.querySelectorAll("textarea:not(.onboarding-chat-wrapper *)")
    )
      .map((t: any) => (t.placeholder || t.value || "").trim())
      .filter((t) => t && t.length > 0)
      .slice(0, 5);

    // 5. Checkboxes
    const checkboxes = Array.from(
      document.querySelectorAll(
        "input[type=checkbox]:not(.onboarding-chat-wrapper *)"
      )
    )
      .map((c: any) => {
        const label =
          c.labels && c.labels.length > 0
            ? c.labels[0].textContent
            : c.name || "Checkbox";
        return `${label}: ${c.checked ? "Marcado" : "Desmarcado"}`;
      })
      .slice(0, 5);

    // 6. Radios
    const radios = Array.from(
      document.querySelectorAll(
        "input[type=radio]:not(.onboarding-chat-wrapper *)"
      )
    )
      .map((r: any) => {
        const label =
          r.labels && r.labels.length > 0
            ? r.labels[0].textContent
            : r.name || "Radio";
        return `${label}: ${r.checked ? "Seleccionado" : "No seleccionado"}`;
      })
      .slice(0, 5);

    // 7. Tablas y Metadatos Inteligentes (Prioridad)
    const smartMetadata = Array.from(
      document.querySelectorAll(
        "[data-ai-context]:not(.onboarding-chat-wrapper *)"
      )
    )
      .map((el) => {
        try {
          return JSON.parse(el.getAttribute("data-ai-context") || "{}");
        } catch (e) {
          return null;
        }
      })
      .filter((m) => m !== null);

    const tablas = Array.from(
      document.querySelectorAll("table:not(.onboarding-chat-wrapper *)")
    )
      .map((t) =>
        t.textContent ? t.textContent.trim().substring(0, 50) + "..." : ""
      )
      .filter((t) => t && t.length > 0)
      .slice(0, 3);

    // 8. Usuario
    let usuario = usuarioModule.mi_usuario as any;
    if (usuarioModule.mi_usuario) {
      usuarioModule.getmiusuario();
      usuario = { nombre: this.userName, email: this.userEmail };
    }

    // 9. Opciones de menú (recursivo)
    const menus = menu_optionModule.menu_options_menu;
    const extractMenuText = (items: any[]): string[] => {
      let texts: string[] = [];
      if (!items) return texts;
      items.forEach((item) => {
        if (item.text) texts.push(item.text);
        if (item.children && item.children.length > 0) {
          texts = texts.concat(extractMenuText(item.children));
        }
      });
      return texts;
    };
    const menus_plano = extractMenuText(menus);

    const description = `El usuario está viendo: "${title}". 
    Metadatos inteligentes detectados: ${JSON.stringify(smartMetadata)}
    Botones disponibles: ${buttons.join(", ")}.
    Inputs: ${inputs.join(", ")}.
    Selects: ${selects.join(", ")}.
    Textareas: ${textareas.join(", ")}.
    Checkboxes: ${checkboxes.join(", ")}.
    Radios: ${radios.join(", ")}.
    Ruta actual: ${this.$route.fullPath}
    Usuario actual: ${usuario ? usuario.nombre : "Anónimo"} (${
      usuario ? usuario.email : "N/A"
    })
    Tablas detectadas: ${tablas.join(", ")}
    Menus disponibles: ${menus_plano.join(", ")}`;

    this.service.provideScreenContext(title, description);

    // Añadimos al historial local para tracking (pero no se muestra)
    this.messages.push({
      role: "system",
      content: description,
      timestamp: new Date(),
    });
  }

  private scrollToBottom() {
    this.$nextTick(() => {
      const body = this.$refs.chatBody as HTMLElement;
      if (body) {
        body.scrollTop = body.scrollHeight;
      }
    });
  }

  private formatContent(content: string): string {
    return content
      .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
      .replace(/`(.*?)`/g, "<code>$1</code>")
      .replace(/\n/g, "<br>");
  }

  private formatTime(date: Date): string {
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }

  public get userName() {
    return authorizationService.userName();
  }

  public get userEmail(): string {
    try {
      const decoded = JwtService.jwtDecode();
      return decoded.email || decoded.unique_name || "";
    } catch {
      return "";
    }
  }

  @Watch("expanded")
  onExpandedChange(val: boolean) {
    if (val) this.scrollToBottom();
  }
}
